import * as React from 'react';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Chat from './Chat';
import {
  ButtonWithLoader,
  LoaderWithContainer,
} from '../../molecules';
import useUserRole from '../../hooks/useUserRole';
import '../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
// Switch replaced with custom toggle

const CommentsChat = ({
  commentsRequest,
  entityData,
  postRequest,
  permit,
  title,
  buttonText,
  isButtonHidden,
  isReadOnly,
  setCommentCreated,
  commentCreated,
  ticketViewOnly = false,
}: // noFile,
Props) => {
  const { isEmployee, isInspector } = useUserRole();
  const [commentValue, setCommentValue] = React.useState(
    '',
  );
  const [isLoading, setLoading] = React.useState(false);
  const [isError, setError] = React.useState('');
  const [messages, setMessages] = React.useState([]);
  const [isSystemMsg, setIsSystemMsg] = React.useState(false);

  const editorRef = React.useRef(null);

  const sortComments = comments => {
    const tempComments = comments;
    // sorting with full datetime
    const sortedcomments = tempComments?.map(comment => {
      return { ...comment, createdAt: new Date(comment.createdAt) };
    })
    .sort((a, b) => b.createdAt - a.createdAt);
    // Transforming with specified  date format
    const transformedComments = sortedcomments?.map(comment => {
      return { ...comment,
createdAt: (`${new Date(comment.createdAt).toLocaleDateString()}, ${new Date(comment.createdAt).toLocaleTimeString([], {
        timeStyle: 'short',
      })}`) };
    });
    setMessages(transformedComments);
  };

  const handleSwitchChange = () => {
    setIsSystemMsg(prev => !prev);
  };

  const loadComments = () => {
    setLoading(true);
    Promise.all([commentsRequest(isSystemMsg)])
      .then(([commentsResponse]) => {
        sortComments(commentsResponse.data.results);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  React.useEffect(() => {
    loadComments();
  }, [isSystemMsg]);

  React.useEffect(() => {
    if (commentCreated) {
      loadComments();
    }
  }, [commentCreated]);

  React.useEffect(() => {
    if (
      /\S/.test(
        editorRef?.current?.getEditor()?.getText() &&
          isError,
      )
    ) {
      setError('');
    }
  }, [commentValue]);

  const sendComment = () => {
    if (
      /\S/.test(editorRef?.current?.getEditor()?.getText())
    ) {
      Promise.all([
        postRequest({
          text: commentValue,
          permit,
          id: entityData?.id || entityData?.pk,
        })
          .then(response => {
            setCommentCreated(response.data);
          })
          .catch(() => {})
          .finally(() => {
            setCommentValue('');
          }),
      ]);
    } else {
      setError('Text is required for Comment');
    }
  };

  return (
    <>
      <Title>
        <h2>{title}</h2>
        <AuditToggle
          type="button"
          onClick={handleSwitchChange}
          $active={isSystemMsg}
        >
          <ToggleTrack $active={isSystemMsg}>
            <ToggleThumb $active={isSystemMsg} />
          </ToggleTrack>
          {isSystemMsg ? 'Showing Audit' : 'Show Audit'}
        </AuditToggle>
      </Title>
      <CommentsWrapper>
        {isLoading && <LoaderWithContainer />}
        {!isLoading && <Chat messages={messages} />}
        {(isEmployee || isInspector) && (
          <EditorWrapper>
            <ReactQuill
              theme="snow"
              modules={{
                toolbar: [
                  [{ size: [] }, { align: [] }],
                  ['bold', 'italic', 'underline'],
                  [{ color: [] }, { background: [] }],
                  ['image'],
                ],
              }}
              ref={editorRef}
              readOnly={isReadOnly}
              value={commentValue}
              onChange={comment => setCommentValue(comment)}
            />
          </EditorWrapper>
        )}
        {!!isError && <Error>{isError}</Error>}
        {!isButtonHidden && (
        <ButtonWrapper>
          <ButtonWithLoader
            disabled={!!isError || isReadOnly || ticketViewOnly}
            loading={false}
            type="button"
            onClick={sendComment}
          >
            {buttonText}
          </ButtonWithLoader>
        </ButtonWrapper>
        )}
      </CommentsWrapper>
    </>
  );
};

interface Props {
  commentsRequest: (isSystemMsg?: boolean) => Promise<any>;
  entityData: any;
  postRequest: (data: any) => Promise<any>;
  // eslint-disable-next-line react/no-unused-prop-types
  noFile?: boolean;
  permit?: number;
  title: string;
  buttonText: string;
  isButtonHidden?: boolean;
  isReadOnly?: boolean;
  commentCreated?: boolean;
  setCommentCreated?: any;
  ticketViewOnly: boolean;
}

const CommentsWrapper = styled.div`
  width: 100%;
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--neutral-200);
  background: var(--neutral-0);

  @media only screen and (max-width: 840px) {
    width: 75vw !important;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: var(--space-4);
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: var(--space-6);
  margin-bottom: var(--space-3);

  h2 {
    font-size: var(--fs-2xl);
    font-weight: var(--fw-semibold);
    color: var(--neutral-900);
    margin: 0;
  }
`;

const Error = styled.p`
  color: var(--danger-500);
  font-size: var(--fs-sm);
  margin-top: var(--space-2);
`;

const EditorWrapper = styled.div`
  .quill {
    margin-top: var(--space-4);
  }
  .ql-toolbar {
    border: 1px solid var(--neutral-300);
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    background: var(--neutral-50);
    padding: var(--space-2) var(--space-3);
    display: flex;
    flex-wrap: wrap;
    gap: 2px;

    .ql-formats {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      margin-right: var(--space-2);
    }

    button {
      width: 32px;
      height: 32px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius-sm);
      border: 0;
      background: transparent;
      color: var(--neutral-600);
      cursor: pointer;
      transition: background-color var(--motion-fast) var(--ease-standard),
        color var(--motion-fast) var(--ease-standard);

      &:hover {
        background: var(--neutral-200);
        color: var(--neutral-900);
      }

      &.ql-active {
        background: var(--primary-50);
        color: var(--primary-700);
      }

      svg {
        width: 18px;
        height: 18px;
      }

      .ql-stroke {
        stroke: currentColor;
      }
      .ql-fill {
        fill: currentColor;
      }
    }

    .ql-picker {
      height: 32px;
      border-radius: var(--radius-sm);
      color: var(--neutral-600);

      .ql-picker-label {
        display: flex;
        align-items: center;
        padding: 0 var(--space-2);
        border: 1px solid var(--neutral-300);
        border-radius: var(--radius-sm);
        font-size: var(--fs-sm);
        height: 32px;
        color: var(--neutral-700);

        &:hover {
          color: var(--primary-600);
          border-color: var(--primary-400);
        }

        svg {
          width: 14px;
          height: 14px;
        }
      }

      .ql-picker-options {
        border: 1px solid var(--neutral-200);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-md);
        background: var(--neutral-0);
        padding: var(--space-1);

        .ql-picker-item {
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-sm);

          &:hover {
            background: var(--primary-50);
            color: var(--primary-700);
          }
        }
      }
    }
  }
  .ql-container {
    height: 150px;
    border: 1px solid var(--neutral-300);
    border-top: 0;
    border-radius: 0 0 var(--radius-md) var(--radius-md);
    font-size: var(--fs-base);
    font-family: inherit;
  }
  .ql-editor {
    color: var(--neutral-800);
  }
  .ql-editor.ql-blank::before {
    color: var(--neutral-400);
    font-style: normal;
  }
`;

const AuditToggle = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3) var(--space-1) var(--space-2);
  border: 1px solid ${({ $active }) => $active ? 'var(--primary-600)' : 'var(--neutral-300)'};
  border-radius: var(--radius-full);
  background: ${({ $active }) => $active ? 'var(--primary-50)' : 'var(--neutral-0)'};
  color: ${({ $active }) => $active ? 'var(--primary-700)' : 'var(--neutral-600)'};
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  font-family: inherit;
  cursor: pointer;
  transition: all var(--motion-fast) var(--ease-standard);

  &:hover {
    border-color: var(--primary-400);
    background: var(--primary-50);
  }
`;

const ToggleTrack = styled.div<{ $active: boolean }>`
  width: 36px;
  height: 20px;
  border-radius: var(--radius-full);
  background: ${({ $active }) => $active ? 'var(--primary-600)' : 'var(--neutral-300)'};
  position: relative;
  transition: background-color var(--motion-fast) var(--ease-standard);
`;

const ToggleThumb = styled.div<{ $active: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: var(--radius-full);
  background: var(--neutral-0);
  position: absolute;
  top: 2px;
  left: ${({ $active }) => $active ? '18px' : '2px'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: left var(--motion-fast) var(--ease-standard);
`;

export default CommentsChat;
